import React, { InputHTMLAttributes } from "react";
import {
  Control,
  Controller,
  FieldPathValue,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import CoreDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { combineHandlers } from "../helper";

interface DatePickerProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  rules?: RegisterOptions<FieldsType>;
  required?: boolean;
  label?: string;
  disableFuture?: boolean;
  placeholder?: string;
  requiredMessage?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  wrapperClassName?: string;
  labelClassName?: string;
  disabled?: boolean;
  onChange?: (value: Date | null) => void;
  disablePast?: boolean;
  defaultValue?: FieldPathValue<FieldsType, Path<FieldsType>>;
}

function DatePicker<FieldsType extends FieldValues>({
  label,
  name,
  control,
  required,
  requiredMessage,
  rules,
  onChange,
  wrapperClassName = "",
  labelClassName = "",
  disableFuture,
  disabled = false,
  defaultValue,
  disablePast = false,
}: DatePickerProps<FieldsType>) {
  return (
    <Controller
      name={name}
      rules={{
        required: requiredMessage || (required && "Please select a date"),
        ...(rules || {}),
      }}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { error } }) => (
        <div className={wrapperClassName}>
          {label && (
            <label
              className={`inline-block text-gray-500 pb-1 text-sm ${labelClassName}`}
            >
              {label}
              {(required || requiredMessage) && (
                <span className="text-red-600 ml-0.5 text-base">*</span>
              )}
            </label>
          )}
          <CoreDatePicker
            placeholderText="DD-MM-YYYY"
            className={`w-full appearance-none border border-gray-300 px-3 py-2.5 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 invalid:border-1 invalid:border-red-500 focus:outline-none focus:shadow-none focus:ring-0 focus:border-gray-900 text-sm bg-gray-50`}
            dateFormat="dd-MMM-YYYY"
            wrapperClassName="w-full"
            minDate={disablePast ? new Date() : undefined}
            maxDate={disableFuture ? new Date() : undefined}
            disabledKeyboardNavigation={false}
            onChange={combineHandlers<Date | null>(field.onChange, onChange)}
            selected={field.value}
            disabled={disabled}
            customInput={<CustomInput inputRef={field.ref} />}
          />
          <p className="text-red-600 font-normal text-sm m-0 p-0 ml-1  bottom-0 left-0 right-0">
            {error?.message}
          </p>
        </div>
      )}
    />
  );
}

const CustomInput = (props) => {
  const { inputRef } = props;
  return <input {...props} ref={inputRef} readOnly={true} />;
};

export default DatePicker;
